// @flow
import * as React from 'react';
import { createContext, useCallback, useEffect, useState } from 'react';
import ReactGA from 'react-ga';
import ReactGTM from 'react-gtm-module';
import { useLocation } from 'react-router-dom';

import {
  GOOGLE_ANALYTICS_ACCOUNT_ID,
  GOOGLE_ANALYTICS_DEBUG_MODE,
  GOOGLE_ANALYTICS_ENABLED,
  GOOGLE_TAG_MANAGER_ACCOUNT_ID,
  GOOGLE_TAG_MANAGER_ENABLED
} from 'shared/config';
import gaEvents from 'shared/utils/googleAnalyticsEvents';

type Props = {
  children: React.Node,
  applicationId?: string
};

export const TrackingContext = createContext<any>({});

const propertyNameToDimension = {
  applicationId: 'dimension14',
  amountRequested: 'dimension24',
  loanPurpose: 'dimension25',
  turnover: 'dimension26',
  companySegment: 'dimension27'
};

export function TrackingProvider({ children, applicationId }: Props) {
  const [initialized, setInitialized] = useState(false);
  const routerLocation = useLocation();

  useEffect(() => {
    if (initialized) return;

    if (GOOGLE_ANALYTICS_ENABLED) {
      ReactGA.initialize(GOOGLE_ANALYTICS_ACCOUNT_ID, {
        debug: GOOGLE_ANALYTICS_DEBUG_MODE
      });
      gaEvents();
    }

    if (GOOGLE_TAG_MANAGER_ENABLED) {
      ReactGTM.initialize({
        gtmId: GOOGLE_TAG_MANAGER_ACCOUNT_ID,
        dataLayerName: 'dataLayer'
      });
    }
    setInitialized(true);
  }, [initialized]);

  useEffect(() => {
    if (!applicationId) return;

    if (GOOGLE_ANALYTICS_ENABLED) {
      ReactGA.set({ [propertyNameToDimension.applicationId]: applicationId });
    }

    if (GOOGLE_TAG_MANAGER_ENABLED) {
      ReactGTM.dataLayer({
        dataLayer: {
          event: 'analytics.wa_pageview',
          loanApplicationIdGPO: `${applicationId}`
        }
      });
    }
  }, [applicationId]);

  useEffect(() => {
    if (!GOOGLE_ANALYTICS_ENABLED) return;

    const page = location.pathname;

    ReactGA.set({ page });
    ReactGA.pageview(page);
    if (window.wa && window.wa.sendPageView) {
      window.wa.sendPageView(page);
    }
  }, [routerLocation.pathname]);

  const setCustomDimensionValue = useCallback((propertyName, value) => {
    if (!GOOGLE_ANALYTICS_ENABLED) return;

    if (!propertyNameToDimension[propertyName] || !value) return;

    ReactGA.set({ [propertyNameToDimension[propertyName]]: value });
  }, []);

  return (
    <TrackingContext.Provider value={{ setCustomDimensionValue }}>
      {children}
    </TrackingContext.Provider>
  );
}
